import { useForm } from 'react-hook-form';
import gYupResolver from '../../technical/form/gYupResolver.ts';
import * as yup from 'yup';
import { updateWidgetState, useWidgetState } from '../../management/customDashboard/CustomWidgetContext.tsx';
import { Stack } from '@mui/joy';
import { FormPredefinedDateRangeInput } from '../../technical/form/FormPredefinedDateRangeInput.tsx';
import type { PredefinedRangeLabel } from '../../predefinedDateRanges.ts';
import { calculateDefaultPresetRange } from '../../management/TilesDateRangeSelector.tsx';
import { defaultRowSpacing } from '../../StackSpacing.ts';
import WidgetSettings from '../../management/customDashboard/WidgetSettings.tsx';
import FormStaticMultiAutocomplete from '../../technical/form/FormStaticMultiAutocomplete.tsx';
import FormSelect from '../../technical/form/FormSelect.tsx';
import FormInput from '../../technical/form/FormInput.tsx';
import {
  createPortfolioDefinitionAutocompleteOptions,
  type PortfolioDefinitionValue,
} from '../../copilot/lab/PortfolioDefinitionService.tsx';
import { usePortfolioBacktestingInputSuspenseQuery, IWindowType } from '../../../generated/graphql.tsx';
import { Suspense, type ReactElement } from 'react';
import Loader from '../../technical/Loader/Loader.tsx';
import { BACKEND_MIN_WINDOW, isWarmUpEnabled, windowTypeOptions } from './PortfolioCorrelations.utils.ts';

const schema = yup.object({
  title: yup.string().required(),
  portfolios: yup
    .array()
    .of(yup.object().required())
    .min(2, 'Select at least 2 portfolios')
    .required('Portfolio selection is required'),
  predefinedDateRange: yup.object().required(),
  windowType: yup.string().oneOf([IWindowType.Expanding, IWindowType.Rolling]).required(),
  windowSize: yup.number().when('windowType', ([windowType], schema) => {
    return windowType === IWindowType.Rolling
      ? schema
          .required('Window size is required for rolling windows')
          .min(BACKEND_MIN_WINDOW, `Window size must be at least ${BACKEND_MIN_WINDOW}`)
      : schema.nullable();
  }),
  warmUpPeriod: yup.number().min(0, 'Warm-up period must be non-negative').required(),
});

interface FormState {
  title: string;
  portfolios: PortfolioDefinitionValue[];
  predefinedDateRange: {
    label: PredefinedRangeLabel;
  };
  windowType: IWindowType;
  windowSize: number | null;
  warmUpPeriod: number;
}

export interface PortfolioCorrelationsWidgetState {
  title: string;
  portfolioIds: string[];
  predefinedDateRange: PredefinedRangeLabel;
  windowType: IWindowType;
  windowSize?: number;
  warmUpPeriod: number;
}

const PortfolioCorrelationsWidgetSettings = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}): ReactElement => {
  const { data } = usePortfolioBacktestingInputSuspenseQuery();
  const portfolioDefinitions = data.portfolio.definitions;
  const initialState = useWidgetState<PortfolioCorrelationsWidgetState>(id);
  const updater = updateWidgetState<PortfolioCorrelationsWidgetState>();

  const portfolioOptions = createPortfolioDefinitionAutocompleteOptions(portfolioDefinitions);

  const initialPortfolios = (initialState.portfolioIds || [])
    .map((portfolioId) => portfolioDefinitions.find((def) => def.id === portfolioId))
    .filter((def): def is NonNullable<typeof def> => def != null)
    .map((def) => ({
      id: def.id,
      name: def.name,
      createdFrom: def.createdFrom,
    }));

  const methods = useForm<FormState>({
    resolver: gYupResolver(schema),
    defaultValues: {
      title: initialState.title,
      portfolios: initialPortfolios,
      predefinedDateRange: calculateDefaultPresetRange(initialState.predefinedDateRange),
      windowType: initialState.windowType || IWindowType.Expanding,
      windowSize: initialState.windowSize || null,
      warmUpPeriod: initialState.warmUpPeriod,
    },
  });

  const windowType = methods.watch('windowType');

  const handleFormSubmit = async (input: FormState): Promise<void> => {
    await updater({
      id,
      state: {
        title: input.title,
        portfolioIds: input.portfolios.map((portfolio) => portfolio.id),
        predefinedDateRange: input.predefinedDateRange.label,
        windowType: input.windowType,
        windowSize: input.windowType === IWindowType.Rolling ? input.windowSize! : undefined,
        warmUpPeriod: input.warmUpPeriod,
      },
      updateBackend: true,
    });

    onFinishedEditing();
  };

  return (
    <WidgetSettings methods={methods} handleFormSubmit={handleFormSubmit} onRemovedWidget={onRemovedWidget}>
      <Stack width={'100%'} rowGap={defaultRowSpacing}>
        <FormStaticMultiAutocomplete
          {...portfolioOptions}
          name="portfolios"
          label="Portfolios"
          width="fullWidth"
          showLabelAboveInput
          placeholder="Select at least 2 portfolios"
          limitTags={3}
        />

        <FormPredefinedDateRangeInput name={'predefinedDateRange'} label={'Date range'} />

        <Stack direction="row" spacing={2}>
          <FormSelect
            name="windowType"
            label="Window type"
            width="normal"
            options={windowTypeOptions}
            showLabelAboveInput
          />

          {windowType === IWindowType.Rolling && (
            <FormInput name="windowSize" label="Window size (days)" type="number" width="normal" showLabelAboveInput />
          )}
        </Stack>

        <FormInput
          name="warmUpPeriod"
          label="Warm-up period (days)"
          type="number"
          width="normal"
          disabled={!isWarmUpEnabled(windowType)}
          showLabelAboveInput
        />
      </Stack>
    </WidgetSettings>
  );
};

export const PortfolioCorrelationsWidgetSettingsContainer = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}): ReactElement => {
  return (
    <Suspense fallback={<Loader />}>
      <PortfolioCorrelationsWidgetSettings
        id={id}
        onFinishedEditing={onFinishedEditing}
        onRemovedWidget={onRemovedWidget}
      />
    </Suspense>
  );
};

export default PortfolioCorrelationsWidgetSettingsContainer;
